import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { scaledCoordinate } from '../drawing.helper'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { green } from '../../../../assets/colors'

export type PositionForecastDrawingState = {
    p1?: Point,
    p2?: Point,
    p3?: Point,
    p4?: Point
}

export type PositionForecastDrawingSettings = {
    accountSize: number,
    risk: number,
    lotSize: number,
    entryPrice: number,
    target: number,
    stopLoss: number,
    targetColor: ColorValue,
    stopLossColor: ColorValue,
    labelColor: ColorValue
}

export type PositionForecastDrawingOptions = {
    longPosition: boolean
}

class PositionForecastDrawingTool extends DrawingBase<PositionForecastDrawingOptions, PositionForecastDrawingSettings, PositionForecastDrawingState> {
    qty = 0
    openTime: Time = 0 as Time
    closeTime: Time = 0 as Time
    target = 0
    targetPercentage = 0
    stopLoss = 0
    stopLossPercentage = 0
    entry = false
    closePrice = 0
    positionOpen: boolean = true

    scaledCoordinates = {
        p1x: 0,
        p2x: 0,
        p1y: 0,
        p2y: 0,
        p3x: 0,
        p3y: 0,
        p4x: 0,
        p4y: 0
    }

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context

        this.calculateAttributes()
        const { p1x, p1y, p2x, p2y, p3x, p4x } = this.scaledCoordinates

        ctx.beginPath()
        ctx.moveTo(p1x, p1y)
        ctx.lineTo(p2x, p2y)
        ctx.lineWidth = window.devicePixelRatio
        ctx.stroke()
        ctx.closePath()

        if (!p3x || !p4x) return

        this.drawTargetSection(ctx)
        this.drawStopLossSection(ctx)
        this.drawPositionText(ctx)

        if (this.entry && this.openTime && this.closeTime) {
            const startX = scaledCoordinate(this.timeToCoordinate(this.openTime) || 0, this.horizontalPixelRatio),
                startY = p1y
            const endX = scaledCoordinate(this.timeToCoordinate(this.closeTime) || 0, this.horizontalPixelRatio),
                endY = scaledCoordinate(this.priceToCoordinate(this.closePrice) || 0, this.verticalPixelRatio)
            ctx.beginPath()
            ctx.moveTo(startX, startY)
            ctx.lineTo(endX, endY)

            ctx.globalAlpha = 1
            ctx.setLineDash([8, 5])
            ctx.stroke()
            ctx.closePath()
        }

    };

    drawTargetSection(ctx: CanvasRenderingContext2D) {
        const { p1x, p1y, p2x, p2y, p3x, p3y, p4x, p4y } = this.scaledCoordinates
        let startX = p3x, startY = p3y, height = Math.abs(p3y - p1y), width = p2x - p1x

        if (!this.options.longPosition) {
            startY = p1y
        }

        ctx.beginPath()
        ctx.globalAlpha = this._settings.targetColor.opacity
        ctx.fillStyle = this._settings.targetColor.color
        ctx.fillRect(startX, startY, width, height)
        ctx.closePath()
        this.drawTargetText(ctx)


    }

    drawTargetText(ctx: CanvasRenderingContext2D) {
        const { p1, p3, p4 } = this._state
        if (!p1 || !p3 || !p4) return
        const { p1x, p2x, p3y } = this.scaledCoordinates
        const { accountSize } = this._settings
        const { longPosition } = this.options
        const amount = accountSize + this.target * Math.max(this.qty, 1)
        const tickSize = (this._chart.securities?.[0]?.tickSize || 5) / (this._chart.securities?.[0]?.priceMultiplier || 100)
        const text = `Target: ${this.target.toFixed(2)} (${this.targetPercentage.toFixed(2)}%) ${(this.target / tickSize).toFixed(2)}, Amount: ${amount.toFixed(2)}`
        ctx.globalAlpha = 1
        ctx.font = 12 * window.devicePixelRatio + 'px sans-serif'
        ctx.textBaseline = longPosition ? 'bottom' : 'top'
        let dim = ctx.measureText(text)
        const textHeight = dim.fontBoundingBoxAscent + dim.fontBoundingBoxDescent
        const textWidth = dim.width

        const startX = (p1x + p2x) / 2 - textWidth / 2
        const startY = longPosition ? p3y - 8 : p3y + 8
        ctx.beginPath()
        ctx.fillStyle = this._settings.targetColor.color
        ctx.roundRect(startX - 4, startY - (longPosition ? textHeight : 0) - 4, textWidth + 8, textHeight + 8, 2)
        ctx.closePath()
        ctx.fill()
        ctx.fillStyle = '#fff'
        ctx.fillText(text, startX, startY)
    }

    drawStopLossSection(ctx: CanvasRenderingContext2D) {
        const { p1x, p1y, p2x, p4y } = this.scaledCoordinates
        let startX = p1x, startY = p1y, height = Math.abs(p4y - p1y), width = p2x - p1x

        if (!this.options.longPosition) {
            startY = p4y
        }

        ctx.beginPath()
        ctx.globalAlpha = this._settings.stopLossColor.opacity
        ctx.fillStyle = this._settings.stopLossColor.color
        ctx.fillRect(startX, startY, width, height)
        ctx.closePath()
        this.drawStopLossText(ctx)
    }

    drawStopLossText(ctx: CanvasRenderingContext2D) {
        const { p1, p3, p4 } = this._state
        if (!p1 || !p3 || !p4) return
        const { p1x, p2x, p4y } = this.scaledCoordinates
        const { accountSize } = this._settings
        const { longPosition } = this.options
        const tickSize = (this._chart.securities?.[0]?.tickSize || 5) / (this._chart.securities?.[0]?.priceMultiplier || 100)
        const amount = accountSize - this.stopLoss * Math.max(this.qty, 1)
        const text = `Stop Loss: ${this.stopLoss.toFixed(2)} (${this.stopLossPercentage.toFixed(2)}%) ${(this.stopLoss / tickSize).toFixed(2)}, Amount: ${amount.toFixed(2)}`
        ctx.globalAlpha = 1
        ctx.font = 12 * window.devicePixelRatio + 'px sans-serif'
        ctx.textBaseline = longPosition ? 'top' : 'bottom'
        let dim = ctx.measureText(text)
        const textHeight = dim.fontBoundingBoxAscent + dim.fontBoundingBoxDescent
        const textWidth = dim.width

        const startX = (p1x + p2x) / 2 - textWidth / 2
        const startY = longPosition ? p4y + 8 : p4y - 8
        ctx.beginPath()
        ctx.fillStyle = this._settings.stopLossColor.color
        ctx.roundRect(startX - 4, startY - (longPosition ? 0 : textHeight) - 4, textWidth + 8, textHeight + 8, 2)
        ctx.closePath()
        ctx.fill()
        ctx.fillStyle = '#fff'
        ctx.fillText(text, startX, startY)
    }

    drawPositionText(ctx: CanvasRenderingContext2D) {
        const { p1, p3, p4 } = this._state
        if (!p1 || !p3 || !p4) return
        const { p1x, p2x, p1y } = this.scaledCoordinates
        const { longPosition } = this.options
        const status = this.entry && this.closeTime ? 'Closed' : 'Open'
        const profit = (longPosition ? 1 : -1) * (this.closePrice - p1.y) * this.qty
        const rrRatio = this.stopLoss ? this.target / this.stopLoss : 0
        const text1 = `${status} P&L: ${profit.toFixed(2)} Qty: ${this.qty}`
        const text2 = `Risk/Reward Ratio: ${rrRatio.toFixed(2)}`
        ctx.globalAlpha = 1
        ctx.font = 12 * window.devicePixelRatio + 'px sans-serif'
        ctx.textBaseline = 'bottom'
        let dim1 = ctx.measureText(text1)
        let dim2 = ctx.measureText(text2)
        const text1Height = dim1.fontBoundingBoxAscent + dim1.fontBoundingBoxDescent
        const text1Width = dim1.width
        const text2Height = dim2.fontBoundingBoxAscent + dim2.fontBoundingBoxDescent
        const text2Width = dim2.width

        const start1X = (p1x + p2x) / 2 - text1Width / 2
        const start2X = (p1x + p2x) / 2 - text2Width / 2
        const start2Y = p1y - 8
        const start1Y = start2Y - 2 - text2Height
        ctx.beginPath()
        ctx.fillStyle = profit >= 0 ? this._settings.targetColor.color : this._settings.stopLossColor.color
        ctx.roundRect(Math.min(start1X, start2X) - 4, start1Y - text1Height - 4, text1Width + 8, text1Height + 10 + text2Height, 2)
        ctx.closePath()
        ctx.fill()
        ctx.fillStyle = '#fff'
        ctx.fillText(text1, start1X, start1Y)
        ctx.fillText(text2, start2X, start2Y)
    }

    calculateAttributes() {
        const { p1, p2, p3, p4 } = this._state
        if (!p1 || !p2) return

        let p1x = this.timeToCoordinate(p1.x)
        let p2x = this.timeToCoordinate(p2.x)
        let p1y = this.priceToCoordinate(p1.y)
        let p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.scaledCoordinates.p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
        }

        if (!p3 || !p4) return

        let p3x = this.timeToCoordinate(p3.x)
        let p3y = this.priceToCoordinate(p3.y)
        let p4x = this.timeToCoordinate(p4.x)
        let p4y = this.priceToCoordinate(p4.y)

        if (p3x && p3y && p4x && p4y) {
            this.scaledCoordinates.p3x = scaledCoordinate(p3x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p3y = scaledCoordinate(p3y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p4x = scaledCoordinate(p4x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p4y = scaledCoordinate(p4y, this.verticalPixelRatio) as Coordinate
        }

        const { accountSize, risk, lotSize } = this._settings
        this.stopLoss = Math.abs(p1.y - p4.y)
        this.stopLossPercentage = this.stopLoss * 100 / p1.y
        this.qty = Math.floor(accountSize * (risk / 100) / (Math.abs(this.stopLoss)))
        const lots = Math.floor(this.qty / lotSize)
        this.qty = lots * lotSize
        this.target = Math.abs(p3.y - p1.y)
        this.targetPercentage = this.target * 100 / p1.y

        const startIndex = this.timeToLogical(p1.x)
        const endIndex = this.timeToLogical(p2.x)
        const entryPrice = p1.y, targetPrice = p3.y, stopLossPrice = p4.y
        this.entry = false
        this.openTime = 0 as Time
        this.closeTime = 0 as Time
        this.closePrice = 0
        const { longPosition } = this.options
        if (startIndex && endIndex) {
            const data = this.getSeries().data()
            for (let c = startIndex; c <= endIndex; c++) {
                if (this.closePrice) continue
                const d = data[c]
                if (!d) return
                if (this.entry) {
                    if ((longPosition && d.high >= targetPrice) || (!longPosition && d.low <= targetPrice)) {
                        this.closeTime = d.time
                        this.closePrice = targetPrice
                    } else if ((longPosition && d.low <= stopLossPrice) || (!longPosition && d.high >= stopLossPrice)) {
                        this.closePrice = stopLossPrice
                        this.closeTime = d.time
                    }
                } else {
                    if ((longPosition && d.low <= entryPrice && d.high >= entryPrice) || (!longPosition && d.high >= entryPrice && d.low <= entryPrice)) {
                        this.openTime = d.time
                        this.entry = true
                    }
                }
            }

            if (!this.closePrice) {
                this.closePrice = data[endIndex].close
            }
        }

    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const { longPosition } = this.options
        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        const priceCoordinate = this.priceToCoordinate(price)
        const timeCoordinate = this.timeToCoordinate(time)

        if (!priceCoordinate || !timeCoordinate) return { ...point }

        if (!this.lastAddedPoint) {
            this._state.p1 = point
            const width = this.canvasWidth / 6 / window.devicePixelRatio
            const height = width / 2
            const p3: Point = {
                id: 'p3',
                x: time,
                y: this.coordinateToPrice((priceCoordinate as number) + (longPosition ? -height : height) as Coordinate)!
            }
            this._state.p3 = p3

            const p4: Point = {
                id: 'p4',
                x: time,
                y: this.coordinateToPrice((priceCoordinate as number) + (longPosition ? height : -height) as Coordinate)!
            }
            this._state.p3 = p3
            this._state.p4 = p4
            const data = this.getSeries().data()
            if (!data.length) return { ...point }
            const [last] = data.slice(-1)
            const lastCoordinate = this.timeToCoordinate(last.time)
            if (!lastCoordinate) return { ...point }
            const p2: Point = {
                id: 'p2',
                x: this.coordinateToTime(((timeCoordinate as number) + width) as Coordinate) as Time,
                y: price
            }
            this._state.p2 = p2
            this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2, p3, p4 } = this._state

        p1 && handles.push(p1)
        p2 && handles.push(p2)
        p3 && handles.push(p3)
        p4 && handles.push(p4)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { p1x, p2x, p3y, p4y } = this.scaledCoordinates
        const minY = Math.min(p3y, p4y)
        const maxY = Math.max(p3y, p4y)
        if (scaledX < p1x || scaledX > p2x || scaledY < minY || scaledY > maxY) return null

        return {
            externalId: this.id,
            zOrder: 'normal'
        }
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
                    break
                case 'p3':
                    this._state.p3 = h
                    break
                case 'p4':
                    this._state.p4 = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        const { longPosition } = this.options

        switch (id) {
            case 'p1':
                this._state.p1!.x = time
                this._state.p1!.y = price
                this._state.p3!.x = time
                this._state.p4!.x = time
                this._state.p2!.y = price
                break
            case 'p2':
                this._state.p2!.x = time
                break
            case 'p3':
                this._state.p3!.y = longPosition ? Math.max(price, this._state.p1!.y) : Math.min(price, this._state.p1!.y)
                break
            case 'p4':
                this._state.p4!.y = longPosition ? Math.min(price, this._state.p1!.y) : Math.max(price, this._state.p1!.y)
                break
        }
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {

        const { p1, p3, p4 } = this._state
        if (!p1 || !p3 || !p4) return []

        return [{
            id: 'target',
            p1: p1.y,
            p2: p3.y,
            fill: true,
            fillColor: this._settings.targetColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.targetColor.color
            }
        }, {
            id: 'stopLoss',
            p1: p4.y,
            p2: p1.y,
            fill: true,
            fillColor: this._settings.stopLossColor.color,
            labelColors: {
                p1: this._settings.stopLossColor.color,
                p2: this._settings.labelColor.color
            }
        }]

    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []

        const sorted = [p1, p2].sort((a, b) => (a.x as number) - (b.x as number))

        return [{
            id: 'time-outer',
            p1: sorted[0].x,
            p2: sorted[1].x,
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }


    getAvailableSettings = (): DrawingSettingGroup<PositionForecastDrawingSettings, SettingValues>[] => {
        return [{
            id: 'inputs',
            displayName: 'Inputs',
            visible: true,
            settings: [
                {
                    name: 'accountSize',
                    label: 'Account Size',
                    type: SettingType.NUMBER,
                    defaultValue: 100000,
                    custom: true,
                    visible: true,
                    range: [1]
                },
                {
                    name: 'lotSize',
                    label: 'Lot Size',
                    type: SettingType.NUMBER,
                    defaultValue: 1,
                    custom: true,
                    visible: true,
                    range: [0]
                },
                {
                    name: 'risk',
                    label: 'Risk (%)',
                    type: SettingType.NUMBER,
                    defaultValue: 50,
                    custom: true,
                    visible: true,
                    range: [0, 100]
                }
            ]
        },
        {
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'targetColor',
                    label: 'Target Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: green,
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'stopLossColor',
                    label: 'Stop Loss Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#f23645',
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#6397d4',
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): PositionForecastDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof PositionForecastDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = [...s.settings.map(setting => setting.name).filter(v => v !== 'labelColor')]
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'positionForecast'
    }
}

export default PositionForecastDrawingTool